import { apiFetch, getAccessToken } from '../lib/api';
import type { CatchesListResponse, CreateCatchRequest, CatchResponse, CatchDetailResponse } from '../types/api';

export function listCatches(params: {
  page?: number;
  pageSize?: number;
  fishTypeId?: number;
  from?: string;
  to?: string;
  sessionId?: number;
  clubId?: number;
}): Promise<CatchesListResponse> {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set('page', String(params.page));
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  if (params.fishTypeId != null) qs.set('fishTypeId', String(params.fishTypeId));
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.sessionId != null) qs.set('sessionId', String(params.sessionId));
  if (params.clubId != null) qs.set('clubId', String(params.clubId));
  const query = qs.toString();
  return apiFetch<CatchesListResponse>(`/api/catches${query ? `?${query}` : ''}`);
}

export function createCatch(req: CreateCatchRequest): Promise<CatchResponse> {
  return apiFetch<CatchResponse>('/api/catches', { method: 'POST', body: req });
}

/** Uploads the (already transcoded) photo bytes for a catch. Sends raw bytes, not multipart. */
export async function uploadCatchPhoto(id: string, photo: Blob): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(`/api/catches/${id}/photo`, {
    method: 'POST',
    headers: {
      'Content-Type': 'image/jpeg',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: photo,
  });
  if (!res.ok) throw new Error(`Photo upload failed (${res.status})`);
}

export function getCatch(id: string): Promise<CatchDetailResponse> {
  return apiFetch<CatchDetailResponse>(`/api/catches/${id}`);
}

/**
 * The photo serve endpoint requires a Bearer header, so a plain <img src> would 401.
 * Fetch it as an authenticated blob and return an object URL (caller must revoke it).
 * Returns null when there is no photo (404).
 * Note: this uses raw fetch (not apiFetch), so it does NOT auto-refresh on a 401 —
 * an access token expiring between page load and this call yields null (photo silently
 * absent). Acceptable for v1; the token is normally seconds old at this point.
 */
export async function fetchCatchPhotoObjectUrl(id: string): Promise<string | null> {
  const token = getAccessToken();
  const res = await fetch(`/api/catches/${id}/photo`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    credentials: 'include',
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function deleteCatch(id: string): Promise<void> {
  return apiFetch<void>(`/api/catches/${id}`, { method: 'DELETE' });
}

export interface UpdateCatchRequest {
  spotId?: number;
  fishTypeId: number;
  lureTypeId?: number;
  latitude: number;
  longitude: number;
  caughtAt: string;
  lengthInches?: number;
  notes?: string;
  isPublic?: boolean;
}

export function updateCatch(id: string, req: UpdateCatchRequest): Promise<CatchResponse> {
  return apiFetch<CatchResponse>(`/api/catches/${id}`, { method: 'PUT', body: req });
}
